import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import theme from '@utils/theme'
import Typography from '@components/typography'

// Define keyframes for entry animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`

const scaleIn = keyframes`
  from {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
`

const scaleOut = keyframes`
  from {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  to {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
`

// Overlay background with fade-in/out animation
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 999;

  &.visible {
    animation: ${fadeIn} 0.3s ease-out forwards;
  }

  &.hidden {
    animation: ${fadeOut} 0.3s ease-in forwards;
  }
`

// Toast Container with different animations for mobile and desktop
const ToastContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 24px;
  z-index: 1000;
  row-gap: 4px;
  text-align: center;

  /* For screens > 640px: Centered with specified width and scale animation */
  top: 50%;
  left: 50%;
  width: 40%;
  max-width: 600px;

  &.visible {
    animation: ${scaleIn} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  &.hidden {
    animation: ${scaleOut} 0.4s cubic-bezier(0.34, 0.56, 0.64, 1) forwards;
  }

  /* Responsive behavior for small screens with slide-up/down animation */
  @media (max-width: 1024px) {
    bottom: 0;
    left: 0;
    right: 0;
    top: auto;
    width: 100%;
    max-width: 100%;
    border-radius: 10px 10px 0 0;
    height: auto;
    border-bottom: none;
    padding: 24px;
    transform: translateY(0);

    &.visible {
      animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    &.hidden {
      animation: ${slideDown} 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
  }
`

// Heading text
const Heading = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #333;
`

// Content text
const Content = styled.p`
  font-size: 16px;
  margin: 0 0 12px 0;
  color: #666;
`

// Action button
const ActionButton = styled.a`
  display: inline-block;
  background-color: #0072c6;
  color: white;
  margin-top: 24px;
  padding: 12px 24px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s;
  cursor: pointer;
  width: 100%;
  text-align: center;

  &:hover {
    background-color: #005da6;
  }
`

const Tag = styled.span`
  color: ${theme.colors.primary};
  font-size: 16px;
  font-weight: 600;
`

const BookTypography = styled(Typography)<any>`
  span {
    color: ${(props) => props.theme.colors.primary};
  }
`
export {
  ToastContainer,
  Heading,
  Content,
  ActionButton,
  Overlay,
  Tag,
  BookTypography,
}
